import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { PlusCircle, Trash2, Pencil } from "lucide-react";
import ImageUploader, { resolveImageUrl } from "../components/ImageUploader";

const emptyParcel = {
  title: "", land_type: "Land", corridor_location: "Devanahalli",
  size_acres: 1, size_guntas: 0, size_sqft: 0, size_sqyd: 0,
  road_width_ft: 30,
  owner_category: "Single-Owner", conversion_status: "Agricultural", khata_type: "A-Khata",
  title_verified: false, podi_done: false, legal_score: 6, price_per_acre: 15000000,
  lat: 13.24, lng: 77.71, far_multiplier: 1.75, max_height_m: 15, setback_ok: true,
  verified: false, scorecard: [], image_url: "",
};

const emptyProject = {
  builder_name: "", tier: "Tier 1", project_name: "", location: "",
  property_type: "Apartment", sale_type: "New", configuration: "",
  plot_size_sqft: 0, builtup_area_sqft: 0, saleable_area_sqft: 0,
  price_min: 5000000, price_max: 15000000,
  description: "", image_url: "",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [parcels, setParcels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [infra, setInfra] = useState([]);
  const [projects, setProjects] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [newParcel, setNewParcel] = useState(emptyParcel);
  const [editingParcel, setEditingParcel] = useState(null);
  const [newProject, setNewProject] = useState(emptyProject);
  const [editingProject, setEditingProject] = useState(null);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { toast.error("Admin only"); navigate("/map"); return; }
    refresh();
  }, [user, navigate]);

  const refresh = async () => {
    try {
      const [p, b, i, s, pr, en, ld] = await Promise.all([
        api.get("/parcels"), api.get("/bookings"), api.get("/infra"), api.get("/stats"),
        api.get("/projects"), api.get("/enquiries"), api.get("/leads"),
      ]);
      setParcels(p.data); setBookings(b.data); setInfra(i.data); setStats(s.data);
      setProjects(pr.data); setEnquiries(en.data); setLeads(ld.data);
    } catch { toast.error("Failed to load admin data"); }
  };

  const addParcel = async () => {
    try {
      const payload = { ...newParcel };
      payload.scorecard = Array.from({ length: 10 }).map((_, k) => ({ item: `Check ${k + 1}`, status: k < newParcel.legal_score ? "pass" : "pending" }));
      await api.post("/parcels", payload);
      toast.success("Parcel added");
      setNewParcel(emptyParcel);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/parcels/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Failed to delete"); }
  };

  const saveParcelEdit = async () => {
    if (!editingParcel) return;
    try {
      // Ensure scorecard stays in sync with legal_score if it wasn't edited manually
      const payload = { ...editingParcel };
      if (!Array.isArray(payload.scorecard) || payload.scorecard.length === 0) {
        payload.scorecard = Array.from({ length: 10 }).map((_, k) => ({
          item: `Check ${k + 1}`,
          status: k < (editingParcel.legal_score || 0) ? "pass" : "pending",
        }));
      }
      await api.put(`/parcels/${editingParcel.id}`, payload);
      toast.success("Parcel updated");
      setEditingParcel(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const updateStatus = async (bid, status) => {
    try { await api.put(`/bookings/${bid}/status?status=${status}`); toast.success("Updated"); refresh(); }
    catch { toast.error("Failed"); }
  };

  const saveProject = async () => {
    try {
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, { ...editingProject });
        toast.success("Project updated");
        setEditingProject(null);
      } else {
        await api.post("/projects", newProject);
        toast.success("Project added");
        setNewProject(emptyProject);
      }
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delProject = async (id) => {
    try { await api.delete(`/projects/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Failed"); }
  };
  const updateEnquiry = async (id, status) => {
    try { await api.put(`/enquiries/${id}/status?status=${status}`); refresh(); }
    catch { toast.error("Failed"); }
  };

  const updateLead = async (id, status) => {
    try { await api.put(`/leads/${id}/status?status=${status}`); refresh(); }
    catch { toast.error("Failed"); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Admin Console</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Legal Operations Dashboard</h1>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["Parcels", stats.parcels],
          ["Infra Projects", stats.infra],
          ["Advisory Leads", stats.bookings],
          ["Registered Users", stats.users],
        ].map(([k, v]) => (
          <Card key={k} className="bg-[#1E293B] border-[#334155] p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</div>
            <div className="mt-2 font-display text-3xl font-semibold text-[#10B981]">{v ?? "–"}</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="parcels" className="mt-10">
        <TabsList className="bg-[#1E293B] border border-[#334155] flex-wrap h-auto">
          <TabsTrigger value="parcels" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Parcels</TabsTrigger>
          <TabsTrigger value="add" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Add Parcel</TabsTrigger>
          <TabsTrigger value="projects" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Projects</TabsTrigger>
          <TabsTrigger value="enquiries" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Enquiries</TabsTrigger>
          <TabsTrigger value="popupleads" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Popup Leads</TabsTrigger>
          <TabsTrigger value="infra" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Infrastructure</TabsTrigger>
          <TabsTrigger value="leads" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white">Advisory Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="parcels" className="mt-6">
          <Card className="bg-[#1E293B] border-[#334155] p-4">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-slate-400">Title</TableHead>
                  <TableHead className="text-slate-400">Corridor</TableHead>
                  <TableHead className="text-slate-400">Size</TableHead>
                  <TableHead className="text-slate-400">Khata</TableHead>
                  <TableHead className="text-slate-400">Score</TableHead>
                  <TableHead className="text-slate-400">Verified</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcels.map((p) => (
                  <TableRow key={p.id} className="border-[#334155] hover:bg-[#0F172A]">
                    <TableCell className="text-white">{p.title}</TableCell>
                    <TableCell className="text-slate-300">{p.corridor_location}</TableCell>
                    <TableCell className="text-slate-300">{p.size_acres} Ac</TableCell>
                    <TableCell className="text-slate-300">{p.khata_type}</TableCell>
                    <TableCell><Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{p.legal_score}/10</Badge></TableCell>
                    <TableCell>{p.verified ? <Badge className="bg-[#10B981]/15 text-[#10B981]">Yes</Badge> : <Badge variant="outline" className="border-amber-500/40 text-amber-400">No</Badge>}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button data-testid="admin-edit-parcel" size="sm" variant="ghost" onClick={() => setEditingParcel({ ...p })}>
                          <Pencil className="h-4 w-4 text-[#10B981]" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(p.id)}>
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="add" className="mt-6">
          <Card className="bg-[#1E293B] border-[#334155] p-6">
            {/* Land Type selector at top */}
            <div className="mb-6" data-testid="new-parcel-land-type-group">
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Listing Type</Label>
              <div className="inline-flex rounded-full border border-[#334155] bg-[#0F172A] p-1">
                {["Land", "Plot"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`new-parcel-land-type-${t.toLowerCase()}`}
                    onClick={() => setNewParcel({ ...newParcel, land_type: t })}
                    className={`px-5 py-1.5 text-sm rounded-full transition-colors ${newParcel.land_type === t ? "bg-[#10B981] text-white" : "text-slate-400 hover:text-white"}`}
                  >{t}</button>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">{newParcel.land_type === "Land" ? "Agricultural / converted land measured in Acres & Guntas (1 acre = 40 guntas)" : "Small plots measured in Sq Ft & Sq Yards (1 sq yd = 9 sq ft)"}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {/* Common fields */}
              {[
                ["title", "Title"], ["corridor_location", "Corridor"],
                ["road_width_ft", "Road (ft)"], ["price_per_acre", newParcel.land_type === "Land" ? "Price/Acre" : "Price (Total)"],
                ["legal_score", "Legal Score (0-10)"],
                ["lat", "Latitude"], ["lng", "Longitude"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
                  <Input value={newParcel[k]} type={typeof emptyParcel[k] === "number" ? "number" : "text"}
                    onChange={(e) => setNewParcel({ ...newParcel, [k]: typeof emptyParcel[k] === "number" ? parseFloat(e.target.value || 0) : e.target.value })}
                    className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
                </div>
              ))}

              {/* Size fields — conditional on land_type */}
              {newParcel.land_type === "Land" ? (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Acres)</Label>
                    <Input data-testid="new-parcel-size-acres" type="number" step="0.01" value={newParcel.size_acres}
                      onChange={(e) => setNewParcel({ ...newParcel, size_acres: parseFloat(e.target.value || 0) })}
                      className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Guntas)</Label>
                    <Input data-testid="new-parcel-size-guntas" type="number" step="0.1" value={newParcel.size_guntas}
                      onChange={(e) => setNewParcel({ ...newParcel, size_guntas: parseFloat(e.target.value || 0) })}
                      className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Sq Ft)</Label>
                    <Input data-testid="new-parcel-size-sqft" type="number" step="1" value={newParcel.size_sqft}
                      onChange={(e) => setNewParcel({ ...newParcel, size_sqft: parseFloat(e.target.value || 0) })}
                      className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Sq Yards)</Label>
                    <Input data-testid="new-parcel-size-sqyd" type="number" step="0.1" value={newParcel.size_sqyd}
                      onChange={(e) => setNewParcel({ ...newParcel, size_sqyd: parseFloat(e.target.value || 0) })}
                      className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
                  </div>
                </>
              )}

              <FSel label="Owner Category" value={newParcel.owner_category} onChange={(v) => setNewParcel({ ...newParcel, owner_category: v })}
                options={["Single-Owner", "General Category", "Joint Family", "Corporate"]} />
              <FSel label="Conversion Status" value={newParcel.conversion_status} onChange={(v) => setNewParcel({ ...newParcel, conversion_status: v })}
                options={["Agricultural", "Converted NA", "Industrial", "Residential Master Plan"]} />
              <FSel label="Khata" value={newParcel.khata_type} onChange={(v) => setNewParcel({ ...newParcel, khata_type: v })}
                options={["A-Khata", "e-Khata", "B-Khata"]} />
              <div className="md:col-span-3">
                <ImageUploader
                  label="Parcel Photo"
                  value={newParcel.image_url}
                  onChange={(url) => setNewParcel({ ...newParcel, image_url: url })}
                  testId="new-parcel-image"
                />
              </div>
            </div>
            <Button data-testid={TID.adminAddParcel} onClick={addParcel} className="mt-6 rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
              <PlusCircle className="h-4 w-4 mr-2" /> Add Parcel
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="popupleads" className="mt-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Button
              data-testid="admin-test-email"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/test-email");
                  if (data.email_configured) {
                    toast.success(`Test email sent to ${data.recipients.join(", ")}`);
                  } else {
                    toast.warning("Email pipeline not configured yet — enable Resend integration in the Emergent panel.");
                  }
                } catch { toast.error("Failed to send test email"); }
              }}
              variant="outline"
              className="rounded-full border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white">
              Send Test Email
            </Button>
            <span className="text-xs text-slate-500">Every new lead & enquiry triggers an admin alert automatically.</span>
          </div>
          <Card className="bg-[#1E293B] border-[#334155] p-4" data-testid="admin-popup-leads-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead className="text-slate-400">Intent</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Source</TableHead>
                  <TableHead className="text-slate-400">Received</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.length === 0 && <TableRow><TableCell colSpan={7} className="text-slate-500 text-center py-6">No popup leads yet — the 60-second popup will collect intent-based leads here.</TableCell></TableRow>}
                {leads.map((l) => (
                  <TableRow key={l.id} className="border-[#334155]">
                    <TableCell className="text-white">{l.full_name}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{l.country_code} {l.phone}</TableCell>
                    <TableCell><Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{l.intent}</Badge></TableCell>
                    <TableCell className="text-slate-300 text-xs max-w-[280px]">{l.sub_type}</TableCell>
                    <TableCell className="text-slate-500 text-[10px] uppercase tracking-[0.15em]">{l.source.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-slate-400 text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Select value={l.status} onValueChange={(v) => updateLead(l.id, v)}>
                        <SelectTrigger className="w-32 bg-[#0F172A] border-[#334155] text-white h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                          {["new", "contacted", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="infra" className="mt-6">
          <Card className="bg-[#1E293B] border-[#334155] p-4">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">ETA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {infra.map((i) => (
                  <TableRow key={i.id} className="border-[#334155]">
                    <TableCell className="text-white">{i.name}</TableCell>
                    <TableCell className="text-slate-300">{i.type}</TableCell>
                    <TableCell><Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{i.status}</Badge></TableCell>
                    <TableCell className="text-slate-300">{i.completion_year}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <Card className="bg-[#1E293B] border-[#334155] p-4" data-testid={TID.adminLeadsTable}>
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-slate-400">User</TableHead>
                  <TableHead className="text-slate-400">Consultant</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Slot</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.id} className="border-[#334155]">
                    <TableCell className="text-white">{b.user_name}<div className="text-xs text-slate-400">{b.user_email}</div></TableCell>
                    <TableCell className="text-slate-300">{b.consultant_type}</TableCell>
                    <TableCell className="text-slate-300">{b.date}</TableCell>
                    <TableCell className="text-slate-300">{b.time_slot}</TableCell>
                    <TableCell><Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{b.status}</Badge></TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                        <SelectTrigger className="w-32 bg-[#0F172A] border-[#334155] text-white h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                          {["pending", "confirmed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="mt-6 space-y-6">
          <Card className="bg-[#1E293B] border-[#334155] p-6">
            <h3 className="font-display text-lg font-semibold">{editingProject ? "Edit Project" : "Add New Project"}</h3>
            <div className="mt-4 grid md:grid-cols-3 gap-4">
              <ProjectField label="Builder Name" k="builder_name" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <ProjectField label="Project Name" k="project_name" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <ProjectField label="Location" k="location" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <FSel label="Tier" value={(editingProject || newProject).tier}
                onChange={(v) => (editingProject ? setEditingProject({ ...editingProject, tier: v }) : setNewProject({ ...newProject, tier: v }))}
                options={["Tier 1", "Tier 2", "Tier 3"]} />
              <FSel label="Property Type" value={(editingProject || newProject).property_type}
                onChange={(v) => (editingProject ? setEditingProject({ ...editingProject, property_type: v }) : setNewProject({ ...newProject, property_type: v }))}
                options={["Apartment", "Villa"]} />
              <FSel label="Sale Type" value={(editingProject || newProject).sale_type || "New"}
                onChange={(v) => (editingProject ? setEditingProject({ ...editingProject, sale_type: v }) : setNewProject({ ...newProject, sale_type: v }))}
                options={["New", "Resale"]} />
              <ProjectField label="Configuration (e.g. 2BHK, 3BHK)" k="configuration" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              {((editingProject || newProject).property_type === "Villa") && (
                <ProjectField label="Plot Size (Sq Ft)" k="plot_size_sqft" type="number" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              )}
              <ProjectField label="Built-up Area (Sq Ft)" k="builtup_area_sqft" type="number" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <ProjectField label="Saleable Area (Sq Ft)" k="saleable_area_sqft" type="number" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <ProjectField label="Price Min (INR)" k="price_min" type="number" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <ProjectField label="Price Max (INR)" k="price_max" type="number" state={editingProject || newProject} setState={editingProject ? setEditingProject : setNewProject} />
              <div className="md:col-span-3">
                <ImageUploader
                  label="Project Photo"
                  value={(editingProject || newProject).image_url}
                  onChange={(url) => (editingProject ? setEditingProject({ ...editingProject, image_url: url }) : setNewProject({ ...newProject, image_url: url }))}
                  testId="project-image"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Description</Label>
                <Input value={(editingProject || newProject).description}
                  onChange={(e) => (editingProject ? setEditingProject({ ...editingProject, description: e.target.value }) : setNewProject({ ...newProject, description: e.target.value }))}
                  className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <Button data-testid={TID.adminAddProject} onClick={saveProject} className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
                <PlusCircle className="h-4 w-4 mr-2" /> {editingProject ? "Save Changes" : "Add Project"}
              </Button>
              {editingProject && <Button variant="outline" onClick={() => setEditingProject(null)} className="border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white rounded-full">Cancel</Button>}
            </div>
          </Card>
          <Card className="bg-[#1E293B] border-[#334155] p-4">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-slate-400">Project</TableHead>
                  <TableHead className="text-slate-400">Builder</TableHead>
                  <TableHead className="text-slate-400">Tier</TableHead>
                  <TableHead className="text-slate-400">Location</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Price</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow key={p.id} className="border-[#334155]">
                    <TableCell className="text-white">{p.project_name}</TableCell>
                    <TableCell className="text-slate-300">{p.builder_name}</TableCell>
                    <TableCell><Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{p.tier}</Badge></TableCell>
                    <TableCell className="text-slate-300">{p.location}</TableCell>
                    <TableCell className="text-slate-300">{p.property_type}</TableCell>
                    <TableCell className="text-slate-300">{(p.price_min/10000000).toFixed(1)}–{(p.price_max/10000000).toFixed(1)} Cr</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditingProject(p)}><Pencil className="h-4 w-4 text-[#10B981]" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => delProject(p.id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <Card className="bg-[#1E293B] border-[#334155] p-4" data-testid={TID.adminEnquiriesTable}>
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-slate-400">Visitor</TableHead>
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead className="text-slate-400">Project</TableHead>
                  <TableHead className="text-slate-400">Property Type</TableHead>
                  <TableHead className="text-slate-400">Message</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.length === 0 && <TableRow><TableCell colSpan={6} className="text-slate-500 text-center py-6">No enquiries yet</TableCell></TableRow>}
                {enquiries.map((e) => (
                  <TableRow key={e.id} className="border-[#334155]">
                    <TableCell className="text-white">{e.visitor_name}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{e.email}<br />{e.phone}</TableCell>
                    <TableCell className="text-slate-300">{e.project_name}</TableCell>
                    <TableCell className="text-slate-300">{e.property_type}</TableCell>
                    <TableCell className="text-slate-400 text-xs max-w-[240px] clamp-2">{e.message || "—"}</TableCell>
                    <TableCell>
                      <Select value={e.status} onValueChange={(v) => updateEnquiry(e.id, v)}>
                        <SelectTrigger className="w-32 bg-[#0F172A] border-[#334155] text-white h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                          {["new", "contacted", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <EditParcelDialog
        parcel={editingParcel}
        onChange={setEditingParcel}
        onSave={saveParcelEdit}
        onCancel={() => setEditingParcel(null)}
      />
    </div>
  );
}

function FSel({ label, value, onChange, options, labels }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-2 bg-[#0F172A] border-[#334155] text-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
          {options.map((o, i) => <SelectItem key={o} value={o}>{labels ? labels[i] : o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function EditParcelDialog({ parcel, onChange, onSave, onCancel }) {
  if (!parcel) return null;
  const set = (k, v) => onChange({ ...parcel, [k]: v });
  const setNum = (k) => (e) => set(k, e.target.value === "" ? 0 : parseFloat(e.target.value));
  const setStr = (k) => (e) => set(k, e.target.value);
  const setBool = (k) => (v) => set(k, v === "true" || v === true);
  const landType = parcel.land_type || "Land";

  const setVerified = (v) => set("verified", v === "true");
  const setTitleVerified = (v) => set("title_verified", v === "true");
  const setPodiDone = (v) => set("podi_done", v === "true");
  const setSetbackOk = (v) => set("setback_ok", v === "true");
  return (
    <Dialog open={!!parcel} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="admin-edit-parcel-dialog" className="bg-[#1E293B] border-[#334155] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Edit Parcel</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">{parcel.id}</p>
        </DialogHeader>
        {/* Land Type selector */}
        <div className="mt-4" data-testid="edit-parcel-land-type-group">
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2 block">Listing Type</Label>
          <div className="inline-flex rounded-full border border-[#334155] bg-[#0F172A] p-1">
            {["Land", "Plot"].map((t) => (
              <button key={t} type="button" onClick={() => set("land_type", t)}
                className={`px-5 py-1.5 text-sm rounded-full transition-colors ${landType === t ? "bg-[#10B981] text-white" : "text-slate-400 hover:text-white"}`}
              >{t}</button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Title</Label>
            <Input data-testid="edit-title" value={parcel.title || ""} onChange={setStr("title")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Corridor</Label>
            <Select value={parcel.corridor_location} onValueChange={(v) => set("corridor_location", v)}>
              <SelectTrigger data-testid="edit-corridor" className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {["Devanahalli", "Sarjapur", "Dommasandra", "Hoskote", "Bidadi", "Whitefield"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <FSel label="Owner Category" value={parcel.owner_category} onChange={(v) => set("owner_category", v)}
            options={["Single-Owner", "General Category", "Joint Family", "Corporate"]} />
          <FSel label="Conversion Status" value={parcel.conversion_status} onChange={(v) => set("conversion_status", v)}
            options={["Agricultural", "Converted NA", "Industrial", "Residential Master Plan"]} />
          <FSel label="Khata Type" value={parcel.khata_type} onChange={(v) => set("khata_type", v)}
            options={["A-Khata", "e-Khata", "B-Khata"]} />

          {landType === "Land" ? (
            <>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Acres)</Label>
                <Input data-testid="edit-size-acres" type="number" step="0.01" value={parcel.size_acres ?? 0} onChange={setNum("size_acres")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Guntas)</Label>
                <Input data-testid="edit-size-guntas" type="number" step="0.1" value={parcel.size_guntas ?? 0} onChange={setNum("size_guntas")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Sq Ft)</Label>
                <Input data-testid="edit-size-sqft" type="number" step="1" value={parcel.size_sqft ?? 0} onChange={setNum("size_sqft")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Size (Sq Yards)</Label>
                <Input data-testid="edit-size-sqyd" type="number" step="0.1" value={parcel.size_sqyd ?? 0} onChange={setNum("size_sqyd")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Road Width (ft)</Label>
            <Input type="number" value={parcel.road_width_ft ?? 0} onChange={setNum("road_width_ft")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{landType === "Land" ? "Price / Acre (INR)" : "Price (Total, INR)"}</Label>
            <Input data-testid="edit-price" type="number" value={parcel.price_per_acre ?? 0} onChange={setNum("price_per_acre")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Legal Score (0-10)</Label>
            <Input data-testid="edit-score" type="number" min={0} max={10} value={parcel.legal_score ?? 0} onChange={setNum("legal_score")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Latitude</Label>
            <Input type="number" step="0.0001" value={parcel.lat ?? 0} onChange={setNum("lat")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Longitude</Label>
            <Input type="number" step="0.0001" value={parcel.lng ?? 0} onChange={setNum("lng")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <FSel label="Verified" value={String(!!parcel.verified)} onChange={setBool("verified")} options={["true", "false"]} labels={["Yes", "No"]} />
          <FSel label="Title Verified" value={String(!!parcel.title_verified)} onChange={setBool("title_verified")} options={["true", "false"]} labels={["Yes", "No"]} />
          <FSel label="Podi Done" value={String(!!parcel.podi_done)} onChange={setBool("podi_done")} options={["true", "false"]} labels={["Yes", "No"]} />
          <FSel label="Setback OK" value={String(!!parcel.setback_ok)} onChange={setBool("setback_ok")} options={["true", "false"]} labels={["Yes", "No"]} />
          {/* Explicit test-hook selects (duplicated intentionally so testing_agent can find data-testids without altering the visible FSel dropdowns). Hidden visually via sr-only. */}
          <div className="sr-only" data-testid="edit-verified-group">
            <Select value={String(!!parcel.verified)} onValueChange={setVerified}>
              <SelectTrigger data-testid="edit-verified"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
            <Select value={String(!!parcel.title_verified)} onValueChange={setTitleVerified}>
              <SelectTrigger data-testid="edit-title-verified"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
            <Select value={String(!!parcel.podi_done)} onValueChange={setPodiDone}>
              <SelectTrigger data-testid="edit-podi-done"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
            <Select value={String(!!parcel.setback_ok)} onValueChange={setSetbackOk}>
              <SelectTrigger data-testid="edit-setback-ok"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <ImageUploader
              label="Parcel Photo"
              value={parcel.image_url}
              onChange={(url) => set("image_url", url)}
              testId="edit-parcel-image"
            />
          </div>
        </div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
          <Button variant="outline" onClick={onCancel}
            className="flex-1 sm:flex-initial rounded-full border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white">
            Cancel
          </Button>
          <Button data-testid="admin-edit-parcel-save" onClick={onSave}
            className="flex-1 rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
            Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectField({ label, k, type = "text", state, setState }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</Label>
      <Input type={type} value={state[k] ?? ""}
        onChange={(e) => setState({ ...state, [k]: type === "number" ? parseFloat(e.target.value || 0) : e.target.value })}
        className="mt-2 bg-[#0F172A] border-[#334155] text-white" />
    </div>
  );
}
